import { inject, Injectable } from '@angular/core'
import { GlobalState } from '../data-access/state/global.state'
import { map, Observable, Subject } from 'rxjs'
import { LogoSetting } from '../models/logo-setting'
import { ChantierState } from '../data-access/state/chantier.state'
import { logoSettings } from '../data-access/constant/logo-settings.constant'
import { PortailEnum } from '../models/enums/portail.enum'

/**
 * This state is meant to manage inter-module communication
 * It acts as a globally accessible cache
 */
@Injectable({ providedIn: 'root' })
export class GlobalDomainService {
    private readonly globalState = inject(GlobalState)
    private readonly chantierState = inject(ChantierState)

    // Selectors
    stopAllLocalSpinners$ = new Subject<void>()
    displayGlobalSpinner$: Observable<boolean> = this.globalState.store.select(
        GlobalState.getShouldDisplayGlobalSpinner,
    )
    globalSpinnerMessage$: Observable<string[]> = this.globalState.store.select(GlobalState.getGlobalSpinnerMessages)

    shouldStartDocumentSpinner(): boolean {
        return (
            this.globalState.store.selectSnapshot(GlobalState.isSignatureTerminee) &&
            !!localStorage.getItem('enveloppeId')
        )
    }

    enveloppeId(): string {
        return localStorage.getItem('enveloppeId')
    }

    // Actions

    stopSpinner(): void {
        this.stopAllLocalSpinners$.next()
    }

    startGlobalSpinner(messages?: string[]): void {
        this.globalState.store.update((state) => ({
            ...state,
            shouldDisplayGlobalSpinner: true,
            globalSpinnerMessages: messages,
        }))
    }

    stopGlobalSpinner(): void {
        this.globalState.store.update((state) => ({
            ...state,
            shouldDisplayGlobalSpinner: false,
        }))
    }

    setSignatureCompleted(isSignatureCompleted: boolean): void {
        this.globalState.store.update((state) => ({
            ...state,
            isSignatureCompleted,
        }))
    }

    setLastEnveloppeId(enveloppeId: string): void {
        localStorage.setItem('enveloppeId', enveloppeId)
    }

    resetSignature(): void {
        localStorage.removeItem('enveloppeId')
        this.globalState.store.update((state) => ({
            ...state,
            isSignatureCompleted: false,
        }))
    }

    currentLogo$: Observable<LogoSetting> = this.chantierState.store.select(ChantierState.getSelectedChantier).pipe(
        map((selectedChantier) => {
            if (!selectedChantier) {
                return logoSettings.get(PortailEnum.portailChantier)
            }
            if (selectedChantier.iris) {
                return logoSettings.get(PortailEnum.portailIris)
            }
            return logoSettings.get(PortailEnum.portailChantier)
        }),
    )
}
